import React from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { setLastViewedTodoType } from "store/actions/todoActions";
import { loadTodosForGroupAsync } from "store/actions/todoActions";
import { setTodoListNavigation } from "store/actions/todoNavigationActions";
//#endregion
//#region types
import { FormFieldConstants, IconDefinitionConstants, ServiceConstants, TodoConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { ExpandableListItemFilterProps } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { ExpandableListItem, TableContext } from "components/common";
import { useLocation, useNavigate } from "react-router";
import useTodosByGroup from "./useTodosByGroup";
import TodoChipsList from "./chipsList";
import useTodoGroupingHelpers from "./useTodoGroupingHelpers";
import useTodoReportFilterDefinition from "components/todos/forms/useReportFilterDefinition";
import { useTodoService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { TodoService } from "services";

const TodosByType: React.FC = () => {
  const todoService = useTodoService();
  const lastViewedType = useSelector((store: AppState) => store.todos.lastViewedType);

  const dispatch = useDispatch();

  const [selectedGroup, setSelectedGroup] = React.useState<WintSuperkollAPIModel.TodoGroup>();
  const [subTypes, setSubTypes] = React.useState<WintSuperkollAPIModel.TodoSubType[]>();
  const [filteredItems, setFilteredItems] = React.useState<(WintSuperkollAPIModel.Todo | undefined)[]>();
  const { todosByGroup: todoItemMap } = useTodosByGroup();
  React.useEffect(() => {
    const lastViewedTypeExists = lastViewedType !== undefined && todoItemMap.has(lastViewedType.group);
    if (todoItemMap.size === 1 && !lastViewedTypeExists) {
      const defaultgroup = todoGroupSummary[0]?.group;
      if (!!defaultgroup) {
        dispatch(setLastViewedTodoType(defaultgroup));
        setSelectedGroup(defaultgroup);
      }
    } else if (lastViewedType !== undefined) {
      setSelectedGroup(lastViewedType.group);
      const filteredItems = todoItemMap
        .get(lastViewedType.group)
        ?.items?.filter(
          (i) => i === undefined || lastViewedType.subTypes === undefined || lastViewedType.subTypes.includes(i.subType)
        );
      if (!filteredItems?.length) {
        dispatch(setLastViewedTodoType(lastViewedType.group));
        return;
      }
      setSubTypes(lastViewedType.subTypes);
      setFilteredItems(filteredItems);
    }
  }, [todoItemMap]);

  const { baseFilter, todoSummary, getMockApiResponse } = useTodoGroupingHelpers();

  const todoGroupSummary =
    todoSummary?.todosGroupSummary
      ?.reduce(function (
        r: { group: WintSuperkollAPIModel.TodoGroup; todosCount: number }[],
        a: WintSuperkollAPIModel.TodoGroupSummary
      ) {
        const groupItem = r.find((item) => item.group === a.group);
        if (groupItem === undefined) {
          r.push({ group: a.group, todosCount: a.todosCount });
        } else {
          groupItem.todosCount = groupItem.todosCount + a.todosCount;
        }
        return r;
      },
      [])
      .sort((a, b) => b.todosCount - a.todosCount) ?? [];

  const location = useLocation();
  const navigate = useNavigate();

  const loadTodoItemsIfMissing = (group: WintSuperkollAPIModel.TodoGroup) => {
    const selectedGroupItem = todoItemMap.get(group);
    if (!selectedGroupItem) return;
    if (!selectedGroupItem.fullyLoaded) {
      dispatch(loadTodosForGroupAsync(group, selectedGroupItem.total));
    }
  };

  const getGroupSubQueries = (group: WintSuperkollAPIModel.TodoGroup): ExpandableListItemFilterProps => {
    const items =
      todoSummary?.todosGroupSummary
        ?.filter((t) => t.group === group)
        .map((m) => ({
          id: m.subType,
          value: todoService.getTodoCardSubTypeFilterNames(m.subType),
        })) ?? [];

    const filterSubQuery = async (items: SelectionItem[]) => {
      if (items.length === 0) {
        setFilteredItems(undefined);
        setSubTypes(undefined);
        return;
      }
      const subTypes = items.map((i) => i.id as WintSuperkollAPIModel.TodoSubType);
      setFilteredItems(todoItemMap.get(group)?.items?.filter((i) => i === undefined || subTypes.includes(i.subType)));
      setSubTypes(subTypes);
      dispatch(setLastViewedTodoType(group, subTypes));
    };

    return {
      items: items,
      filterFunc: filterSubQuery,
      selectedItems: lastViewedType?.group === group ? subTypes : undefined,
    };
  };

  const onExpanded = (group: WintSuperkollAPIModel.TodoGroup, panel?: string) => {
    setFilteredItems(undefined);
    setSelectedGroup(group);
    setSubTypes(undefined);
    dispatch(setLastViewedTodoType(panel ? group : undefined));
  };

  const { filterQueryStateHandler } = useTodoReportFilterDefinition();

  const onGoToItemClick = async (
    group: WintSuperkollAPIModel.TodoGroup,
    allowSubTypeFiltering: boolean,
    index: number
  ) => {
    setSelectedGroup(group);

    dispatch(
      setTodoListNavigation({
        indexToOpen: index,
        pageToOpen: 0,
        numPerPage: todoGroupSummary.find((t) => t.group === group)?.todosCount ?? 0,
        loadNext: () => {
          return drawerFilterFunc(group, allowSubTypeFiltering ? subTypes : undefined).then((res) => ({
            items: res?.data.items ?? [],
            totalItems: res?.data.totalItems ?? 0,
          }));
        },
        todoCanIncludeInNavigation: (todo: WintSuperkollAPIModel.Todo) =>
          todo.group === group && (!allowSubTypeFiltering || !subTypes?.length || subTypes.includes(todo.subType)),
      })
    );

    const urlParams: URLSearchParams = new URLSearchParams();
    filterQueryStateHandler.groups?.urlEncode(urlParams, undefined, [{ id: group, value: "" }]);
    if (allowSubTypeFiltering && subTypes?.length)
      filterQueryStateHandler.subTypes?.urlEncode(
        urlParams,
        undefined,
        subTypes?.map((subType) => ({ id: subType, value: "" }))
      );
    filterQueryStateHandler.snoozedOnly?.urlEncode(urlParams, undefined, false);

    navigate(`${UrlHelper.formatPath(`/Todo/${index}`)}?${urlParams.toString()}`, {
      state: { background: location },
    });
  };

  const goToAllTodosByTypeAndSubType = async (
    group: WintSuperkollAPIModel.TodoGroup,
    subTypes?: WintSuperkollAPIModel.TodoSubType[]
  ) => {
    let urlTodoPath = `${FormFieldConstants.Todo.Filter.Groups}=${group}&${FormFieldConstants.Todo.Filter.SnoozedOnly}=0`;

    if (subTypes?.length && group === selectedGroup)
      urlTodoPath += `&${FormFieldConstants.Todo.Filter.SubTypes}=${subTypes.join("%252C")}`;

    navigate(UrlHelper.formatPath(`/Reports/AllToDos?${urlTodoPath}`), {
      state: { background: location },
    });
  };

  React.useEffect(() => {
    if (selectedGroup === undefined) return;
    loadTodoItemsIfMissing(selectedGroup);
  }, [todoItemMap, selectedGroup]);

  const drawerFilterFunc = React.useCallback(
    async (selectedGroup: WintSuperkollAPIModel.TodoGroup, subTypes?: WintSuperkollAPIModel.TodoSubType[]) => {
      const selectedGroupItems = todoItemMap.get(selectedGroup!);
      if (selectedGroupItems?.fullyLoaded) {
        return getMockApiResponse(
          (subTypes?.length && filteredItems
            ? filteredItems
            : todoItemMap.get(selectedGroup!)?.items ?? []) as WintSuperkollAPIModel.Todo[]
        );
      } else {
        return await TodoService.getTodosByFilter({
          ...ServiceConstants.DefaultFilter,
          groups: [selectedGroup],
          subTypes: subTypes,
          types: TodoConstants.SupportingTodoTypes,
          page: 0,
          numPerPage: (selectedGroupItems?.total ?? 0) + 10,
          excludeSnoozed: true,
        }).then((res) => res.result);
      }
    },
    [filteredItems, todoItemMap]
  );

  return todoSummary === undefined ? (
    <></>
  ) : (
    <TableContext tableKey={"todosByGroup"} filter={baseFilter}>
      <Grid container rowGap={1}>
        {todoGroupSummary?.map((item) =>
          item.todosCount ? (
            <ExpandableListItem
              key={item.group}
              id={item.group.toString()}
              title={todoService.getGroupTypeText(item.group)}
              headerSubContent={`${item.todosCount} ${LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Common.Pc
              )}`}
              headerHoverActions={[
                {
                  icon: IconDefinitionConstants.WEmpty,
                  description: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.View)} 1/${
                    item.todosCount
                  }`,
                  onClickHandler: onGoToItemClick.bind(null, item.group, false, 0),
                },
                {
                  icon: IconDefinitionConstants.WEmpty,
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ViewAll),
                  onClickHandler: goToAllTodosByTypeAndSubType.bind(null, item.group, subTypes),
                  showOnExpand: true,
                },
              ]}
              listFilterProps={item.todosCount > 1 ? getGroupSubQueries(item.group) : undefined}
              expanded={lastViewedType?.group.toString()}
              onChange={onExpanded.bind(null, item.group)}
            >
              <TodoChipsList
                todoItems={(filteredItems ?? todoItemMap.get(item.group)?.items)?.slice(0, 10)}
                total={filteredItems?.length ?? todoItemMap.get(item.group)?.total}
                loadingItemsCount={item.todosCount}
                onGoToItemClick={onGoToItemClick.bind(null, item.group, true)}
                goToAllTodosByType={goToAllTodosByTypeAndSubType.bind(null, item.group, subTypes)}
              />
            </ExpandableListItem>
          ) : (
            <></>
          )
        )}
      </Grid>
    </TableContext>
  );
};

export default TodosByType;
